import { Box, Collapse, InputAdornment, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, MOTION } from "@/theme/tokens";
import { type FlatSwitchOption, SystemFlatSwitch } from "@/ui/Clickable/SystemFlatSwitch";
import { FilterFieldRow } from "@/ui/Filters/FilterFieldRow";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import { formatPredictedCost } from "@/utils/costPrediction";
import { GapsPostIncentive, POST_BONUS_MAX } from "./useGapsPostShiftForm";

type Props = {
	incentive: GapsPostIncentive;
	onIncentiveChange: (next: GapsPostIncentive) => void;
	bonusText: string;
	onBonusTextChange: (next: string) => void;
	/** The amount as a number, `NaN` while the field is empty or unreadable. */
	bonus: number;
	isBonusValid: boolean;
	neededNurses: number;
	/** The sheet's test-id stem — `{testIdPrefix}-incentive`, `-bonus`, `-bonus-hint`… — so the post and posted sheets keep their own. */
	testIdPrefix: string;
};

/** The switch at the sheet's field scale (its defaults are sized for a dense header). */
const SWITCH_FONT_SIZE = { xs: FONT_SIZE.EMPHASIS };

/**
 * The pick-up incentive (P3-9): nothing, or a flat amount per person — the classic screen's incentive pay without its
 * demo-only percentage mode. The amount field opens under the switch, and the line under it says what the number
 * means across the post ("$50 each — $100 if both fill"), because "amount" alone does not say per person or in total.
 */
export const GapsPostIncentiveField: FC<Props> = ({
	incentive,
	onIncentiveChange,
	bonusText,
	onBonusTextChange,
	bonus,
	isBonusValid,
	neededNurses,
	testIdPrefix,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const options: FlatSwitchOption<GapsPostIncentive>[] = [
		{ label: t("gaps.postSheet.incentiveNone"), value: GapsPostIncentive.None, "data-testid": `${testIdPrefix}-incentive-none` },
		{ label: t("gaps.postSheet.incentiveFlat"), value: GapsPostIncentive.Flat, "data-testid": `${testIdPrefix}-incentive-flat` },
	];
	const hasAmount = incentive === GapsPostIncentive.Flat && Number.isFinite(bonus) && bonus > 0;

	return (
		<FilterFieldRow label={t("gaps.postSheet.incentive")} dataTestId={`${testIdPrefix}-incentive`}>
			<SystemFlatSwitch<GapsPostIncentive>
				options={options}
				activeIndexOrValue={incentive}
				onSwitch={(option) => onIncentiveChange(option.value)}
				delayCallback={0}
				equalWidthOptions
				textWrap="nowrap"
				rootWidth="100%"
				// A form field in a sheet, not a dense header toggle: the sheet's own type size and a field-height hit area.
				fontSizeOverrides={SWITCH_FONT_SIZE}
				paddingOverrides={{ xs: theme.spacing(1.125, 1.5) }}
			/>
			<Collapse in={incentive === GapsPostIncentive.Flat} timeout={MOTION.DURATION.ENTRANCE_MD} unmountOnExit>
				<Box sx={{ pt: 1.25 }}>
					<SystemValidationTextField
						value={bonusText}
						onChange={(event) => onBonusTextChange(event.target.value)}
						label={t("gaps.postSheet.amountPerPerson")}
						placeholder="0"
						inputType="number"
						maxValue={POST_BONUS_MAX}
						reserveHeightForHelperText={false}
						fullWidth
						inputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
						injectedProps={{ slotProps: { htmlInput: { inputMode: "decimal", min: 0, "data-testid": `${testIdPrefix}-bonus-input` } } }}
						dataTestId={`${testIdPrefix}-bonus`}
					/>
					<Typography
						data-testid={`${testIdPrefix}-bonus-hint`}
						sx={{ mt: 0.75, fontSize: FONT_SIZE.CAPTION, lineHeight: 1.45, color: isBonusValid || !bonusText ? INK.MUTED : "error.main" }}
					>
						{hasAmount
							? neededNurses > 1
								? t("gaps.postSheet.bonusHintMany", {
										amount: formatPredictedCost(bonus),
										total: formatPredictedCost(bonus * neededNurses),
										count: neededNurses,
									})
								: t("gaps.postSheet.bonusHintOne", { amount: formatPredictedCost(bonus) })
							: t("gaps.postSheet.bonusHintEmpty", { max: formatPredictedCost(POST_BONUS_MAX) })}
					</Typography>
				</Box>
			</Collapse>
		</FilterFieldRow>
	);
};
